import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Skeleton,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Link as LinkIcon,
  Photo as PhotoIcon,
  Event as EventIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { DashboardService, Activity } from '../../services/dashboardService';
import { useNavigate } from 'react-router-dom';

/**
 * RecentActivityCard Component
 *
 * Displays recent changes and activities in the family tree
 *
 * Features:
 * - Shows last 10 activities from ChangeTrackingService
 * - User avatar and name
 * - Action description with timestamp
 * - Click to navigate to related item
 * - Auto-refresh capability
 * - Loading and error states
 *
 * @component
 * @returns {JSX.Element} Rendered activity card
 */
export const RecentActivityCard: React.FC = () => {
  // ===== State =====
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // ===== Effects =====
  useEffect(() => {
    fetchActivities();
  }, []);

  // ===== Helper Functions =====
  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await DashboardService.getRecentActivity(10);
      setActivities(data);
    } catch (err: any) {
      console.error('Failed to load activities:', err);
      // Don't show error for permission issues - just show empty state
      if (err.message?.includes('permission')) {
        setActivities([]);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (iconName?: string) => {
    switch (iconName) {
      case 'person': return <PersonIcon />;
      case 'link': return <LinkIcon />;
      case 'photo': return <PhotoIcon />;
      case 'event': return <EventIcon />;
      default: return <EditIcon />;
    }
  };

  // ===== Event Handlers =====
  const handleActivityClick = (activity: Activity) => {
    if (activity.link) {
      navigate(activity.link);
    }
  };

  const handleRefresh = () => {
    fetchActivities();
  };

  // ===== Render Loading =====
  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardHeader title="Recent Activity" />
        <CardContent>
          <List>
            {[1, 2, 3, 4, 5].map(i => (
              <ListItem key={i}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={40} height={40} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton width="60%" />}
                  secondary={<Skeleton width="40%" />}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  }

  // ===== Render Error =====
  if (error) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardHeader title="Recent Activity" />
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // ===== Render Empty State =====
  if (activities.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardHeader
          title="Recent Activity"
          action={
            <IconButton onClick={handleRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          }
        />
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No recent activity
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Changes will appear here
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // ===== Render =====
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="Recent Activity"
        action={
          <IconButton onClick={handleRefresh} size="small" title="Refresh">
            <RefreshIcon />
          </IconButton>
        }
      />
      <CardContent sx={{ pt: 0, maxHeight: 500, overflow: 'auto' }}>
        <List disablePadding>
          {activities.map((activity, index) => (
            <ListItem
              key={activity.id}
              alignItems="flex-start"
              sx={{
                cursor: activity.link ? 'pointer' : 'default',
                '&:hover': activity.link ? {
                  backgroundColor: 'action.hover',
                } : undefined,
                borderRadius: 1,
                mb: 0.5,
              }}
              onClick={() => handleActivityClick(activity)}
            >
              <ListItemAvatar>
                <Avatar sx={{ backgroundColor: 'primary.light', color: 'primary.main' }}>
                  {getActivityIcon(activity.icon)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    <strong>{activity.userName}</strong> {activity.action}
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {activity.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                      {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};
